import {
  FC,
  useState,
  useLayoutEffect,
  forwardRef,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  FocusEvent as ReactFocusEvent,
} from 'react';
import { ClickAwayListener } from '@mui/material';
import {
  FAIcon,
  TooltipPreference,
  Tooltip,
  Theme,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  useBreakpoint,
  Drawer,
  IconStyle,
} from '../../';
import ARIA_LABELS from '../../AccessibiltyLabels';
import { InputSize } from '../Input/types/enums';
import { DrawerDirections } from '../../Drawer/Drawer';
import { DateObj, DatePickerProps } from './types/propTypes';
import Calendar from './Calendar/Calendar';
import styles from './DatePicker.module.scss';

const DatePicker: FC<DatePickerProps> = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      onChange,
      inputLabel,
      labelInfo = '',
      labelInfoPreference = TooltipPreference.RIGHT,
      placeholder = '',
      cancelLabel = 'Cancel',
      scheduleLabel = 'Confirm',
      onCalendarToggle,
      actionButtonStyle,
      menuContainerStyle,
      onSelect,
      showTime = false,
      selectRange = false,
      value,
      valueTo,
      displayText,
      description,
      calendarHeading,
      calendarFooter,
      inputContainerHeight,
      ariaLabel = ARIA_LABELS.DATEPICKER,
      calendarContainerStyling,
      showDateContainer = true,
      disabled,
      readOnly,
      containerWidth,
      onClear,
      allowRemove = false,
      required = false,
      inputSize = InputSize.MEDIUM,
      contentHeight,
      ...restProps
    },
    ref,
  ) => {
    const [dateTime, setDateTime] = useState<Date>();
    const [dateTimeTo, setDateTimeTo] = useState<Date>();
    const [dateTimeText, setDateTimeText] = useState('');
    const [showCalendar, setShowCalendar] = useState(false);
    const [scheduledDateTime, setScheduledDateTime] = useState<DateObj>();
    const [scheduledDateTimeTo, setScheduledDateTimeTo] = useState<DateObj>();

    useLayoutEffect(() => {
      const initialDate = value ?? new Date();
      const roundedInitialDate = new Date(Math.round(initialDate.getTime() / 900000) * 900000);

      setDateTime(roundedInitialDate);
      setScheduledDateTime(
        value
          ? {
              year: roundedInitialDate.getFullYear(),
              month: roundedInitialDate.getMonth(),
              date: roundedInitialDate.getDate(),
              time: roundedInitialDate.toLocaleString('en-US', {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
              }),
            }
          : {
              year: 0,
              month: 0,
              date: 0,
              time: '',
            },
      );
    }, [value]);

    useLayoutEffect(() => {
      const roundedEndDate = valueTo ? new Date(Math.round(valueTo.getTime() / 900000) * 900000) : new Date(0);
      setDateTimeTo(roundedEndDate);
      setScheduledDateTimeTo(
        valueTo
          ? {
              year: roundedEndDate.getFullYear(),
              month: roundedEndDate.getMonth(),
              date: roundedEndDate.getDate(),
              time: roundedEndDate.toLocaleString('en-US', {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
              }),
            }
          : {
              year: 0,
              month: 0,
              date: 0,
              time: '',
            },
      );
    }, [valueTo]);

    // const { year, month, date, time } = scheduledDateTime;
    // const { year, month, date, time } = scheduledDateTimeTo;
    useLayoutEffect(() => {
      let _dateTimeText = '';
      if (scheduledDateTime) {
        if (scheduledDateTime.date) {
          _dateTimeText = `${scheduledDateTime.date}/${scheduledDateTime.month + 1}/${scheduledDateTime.year} `;
        }

        if (showTime) {
          _dateTimeText += scheduledDateTime.time;
        }
      }

      if (scheduledDateTimeTo) {
        if (selectRange) {
          if (scheduledDateTimeTo.date) {
            _dateTimeText += ` to ${scheduledDateTimeTo.date}/${scheduledDateTimeTo.month + 1}/${
              scheduledDateTimeTo.year
            } `;
          }

          if (showTime) {
            _dateTimeText += scheduledDateTimeTo.time;
          }
        }
      }

      setDateTimeText(_dateTimeText);
    }, [scheduledDateTime, scheduledDateTimeTo]);

    const handleCalendarVisibility = (e: ReactKeyboardEvent | ReactMouseEvent, show: boolean) => {
      setShowCalendar(show);
      onCalendarToggle?.(e);
    };

    const handleSchedule = (e: ReactKeyboardEvent | ReactMouseEvent) => {
      if (dateTime) {
        onChange?.(dateTime, selectRange ? dateTimeTo : undefined, e);
        setScheduledDateTime({
          year: dateTime.getFullYear(),
          month: dateTime.getMonth(),
          date: dateTime.getDate(),
          time: dateTime.toLocaleString('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
          }),
        });
      }

      if (dateTimeTo) {
        if (selectRange && dateTimeTo.getTime() !== new Date(0).getDate()) {
          setScheduledDateTimeTo({
            year: dateTimeTo.getFullYear(),
            month: dateTimeTo.getMonth(),
            date: dateTimeTo.getDate(),
            time: dateTimeTo.toLocaleString('en-US', {
              hour: 'numeric',
              minute: 'numeric',
              hour12: true,
            }),
          });
        }
      }

      handleCalendarVisibility(e, false);
    };

    const actionButtons = (
      <div className={styles['action-button-container']} style={{ ...actionButtonStyle }}>
        <span className={styles['action-button']}>
          <Button
            title={cancelLabel}
            type={ButtonTypes.BUTTON}
            theme={Theme.BLACK}
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyles.OUTLINE}
            onClick={e => handleCalendarVisibility(e, false)}
          />
        </span>
        <span className={styles['action-button']}>
          <Button
            title={scheduleLabel}
            type={ButtonTypes.BUTTON}
            theme={Theme.BLACK}
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyles.SOLID}
            onClick={handleSchedule}
            data-testid="schedule-btn"
          />
        </span>
      </div>
    );

    const handleDateTimeChange = (
      date?: Date,
      dateTo?: Date,
      e?: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent,
    ) => {
      if (showDateContainer) {
        setDateTime(date);
      }
      if (selectRange && dateTo) {
        setDateTimeTo(dateTo);
      }
      onSelect?.(e);
    };

    const handleMonthYearChange = (
      year: number,
      month: number,
      e: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent,
    ) => {
      setDateTime(new Date(year, month));
    };

    const calendarComponent = (
      <>
        {calendarHeading && <h3 className={styles['calendar-heading']}>{calendarHeading}</h3>}
        <Calendar
          {...restProps}
          onChange={handleDateTimeChange}
          showTime={showTime}
          selectRange={selectRange}
          value={dateTime}
          valueTo={dateTimeTo}
          forwardedRef={ref}
          showDateContainer={showDateContainer}
          onNavigate={handleMonthYearChange}
        />
      </>
    );

    const descriptionContent = description && <p className={styles['description-container']}>{description}</p>;

    const footerContent = calendarFooter && <div className={styles['calendar-footer']}>{calendarFooter}</div>;

    const menuContent = (
      <div style={{ ...menuContainerStyle }}>
        <div
          className={styles['menu-container']}
          style={{ width: containerWidth ? containerWidth : undefined, ...calendarContainerStyling }}
        >
          {descriptionContent}
          {calendarComponent}
          {footerContent}
          {actionButtons}
        </div>
      </div>
    );

    const drawerContent = (
      <>
        <Drawer
          header={<span>{inputLabel}</span>}
          footer={actionButtons}
          closeIcon
          className={styles['mobile-drawer']}
          bottomGap={0}
          onClose={() => setShowCalendar(false)}
          contentHeight={contentHeight}
          direction={DrawerDirections.FULLHEIGHT}
        >
          {descriptionContent}
          <div className={styles['calendar-wrapper']}>{calendarComponent}</div>
          {footerContent}
        </Drawer>
      </>
    );

    const viewPortType = useBreakpoint();

    return (
      <div className={`${styles['date-picker-container']} ${styles[inputSize]}`} data-testid="date-picker-container" >
        {inputLabel && (
          <p className={styles['input-label']}>
            {inputLabel}
            {required && <span className={styles['input-label-red']}>*</span>}
            {labelInfo && (
              <Tooltip
                message={labelInfo}
                preference={labelInfoPreference}
                className={styles['date-picker-tool-tip']}
              />
            )}
          </p>
        )}
        <div
          className={
            styles['input-container'] +
            ` ${disabled ? styles['disabled'] : ''}` +
            ` ${readOnly ? styles['read-only'] : ''}`
          }
          style={inputContainerHeight ? { height: inputContainerHeight } : {}}
          data-testid="input-container"
          onClick={e => handleCalendarVisibility(e, !showCalendar)}
          onKeyDown={e => handleCalendarVisibility(e, !showCalendar)}
          tabIndex={0}
          role="button"
          aria-label={ariaLabel}
        >
          <span
            className={styles['placeholder-container']}
            data-value={displayText || dateTimeText || 'placeholderText'}
          >
            {displayText || dateTimeText || placeholder}
          </span>

          {(displayText || dateTimeText) && allowRemove ? (
            <span className={styles['close-icon']}>
              <FAIcon
                iconName={'xmark'}
                iconStyle={IconStyle.SOLID}
                data-testid="clear-icon-test-id"
                onClick={e => {
                  onClear?.(e);
                }}
                className={styles['icon']}
              />
            </span>
          ) : (
            <FAIcon iconName={'calendar'} className={styles['icon']} />
          )}
        </div>
        {!disabled && !readOnly && showCalendar && (
          <ClickAwayListener onClickAway={() => setShowCalendar(false)}>
            <div>{viewPortType === 'xs' || viewPortType === 'sm' ? drawerContent : menuContent}</div>
          </ClickAwayListener>
        )}
      </div>
    );
  },
);
DatePicker.displayName = 'DatePicker';
export { Calendar };
export default DatePicker;
